import { SystemStyleObject } from '@chakra-ui/react'

import { fontFamilies } from '../../theme/fontFamilies'
import type { ColorScheme } from '../../types'

export type { ColorScheme }

export const tabListStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'row',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor:
    colorScheme === 'light' ? 'light.border.tab-toggle' : 'border.tab-toggle',
})

// The close affordance on a closable tab. Rendered as a span (not a button) so
// it can live inside the tab's own <button> without nesting interactive elements.
export const tabCloseStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => {
  const isDark = colorScheme !== 'light'

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ml: '8px',
    borderRadius: '2px',
    cursor: 'pointer',
    color: isDark ? 'icon.secondary' : 'light.icon.secondary',
    _hover: {
      color: isDark ? 'icon.primary' : 'light.icon.primary',
    },
  }
}

export const tabStyles = (
  isActive: boolean,
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => {
  const isDark = colorScheme !== 'light'

  return {
    display: 'inline-flex',
    alignItems: 'center',
    px: '16px',
    h: '36px',
    // A raw <button> doesn't inherit the app font; tab labels are the
    // design's Label style (Söhne Kräftig).
    fontFamily: fontFamilies.soehneKraftig,
    fontSize: '13px',
    fontWeight: '500',
    lineHeight: '1',
    cursor: isActive ? 'default' : 'pointer',
    background: 'none',
    border: 'none',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: isActive
      ? isDark
        ? 'brand.primary'
        : 'light.brand.primary'
      : 'transparent',
    marginBottom: '-1px',
    color: isActive
      ? isDark
        ? 'text.primary'
        : 'light.text.primary'
      : isDark
      ? 'text.secondary'
      : 'light.text.secondary',
    transition: 'color 0.15s ease, border-color 0.15s ease',
    _hover: {
      color: isDark ? 'text.primary' : 'light.text.primary',
    },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: isDark
        ? 'interactive.focus-ring'
        : 'light.interactive.focus-ring',
      outlineOffset: '-2px',
      borderRadius: '2px',
    },
  }
}

/**
 * Applied on top of `tabStyles` for a tab that cannot be selected — used while
 * work started on another tab is still running.
 */
export const disabledTabStyles: SystemStyleObject = {
  cursor: 'not-allowed',
  opacity: 0.5,
}
